import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import { useGetActivityReward } from '~/bootcamp/Activities/hooks/useGetActivityReward'
import RewardSection from '~/bootcamp/pages/BootcampView/BootcampContent/RewardSection'

import appConfig from '~/config/app'
import { selectIsPreSignup } from '~/onboarding/redux/onboarding.slice'
import { useAppSelector } from '~/redux/hooks'

import RewardsChest from '~/shared/components/RewardsChest/RewardsChest'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

interface RewardChestScreenProps {
  onClose?: () => void
}

export default function RewardChestScreen({ onClose }: RewardChestScreenProps): React.ReactElement {
  const { t } = useTranslation('avatar')
  const ref = useRef<HTMLDivElement>(null)
  const hasCalledOnClose = useRef(false)
  const isPreSignupFlow = useAppSelector(selectIsPreSignup)

  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const [showChest, setShowChest] = useState(false)

  const handleAnimationComplete = (): void => {
    if (!hasCalledOnClose.current) {
      hasCalledOnClose.current = true
      onClose?.()
    }
  }

  useEffect(() => {
    let timeoutShow: NodeJS.Timeout
    let timeoutHide: NodeJS.Timeout

    if (ref.current) {
      timeoutShow = setTimeout(() => {
        ref.current?.classList.add('opacity-0')
        if (!hasCalledOnClose.current) {
          hasCalledOnClose.current = true
          onClose?.()
        }
      }, 3000)

      timeoutHide = setTimeout(() => {
        ref.current?.classList.remove('opacity-0')
      }, 10)
    }

    const delay: NodeJS.Timeout = setTimeout(() => {
      setShowChest(true)
    }, 250)

    return () => {
      clearTimeout(timeoutShow)
      clearTimeout(timeoutHide)
      clearTimeout(delay)
      hasCalledOnClose.current = false
    }
  }, [onClose])

  const handleClick = (): void => {
    if (!hasCalledOnClose.current) {
      hasCalledOnClose.current = true
      onClose?.()
    }
  }

  const bgSize = isMobile ? 'bg-full' : 'bg-cover'
  const rewards = useGetActivityReward()
  return (
    <>
      {isPreSignupFlow && (
        <div className="fixed inset-0 z-30 backdrop-filter backdrop-blur-none bg-black/80 pointer-events-none" />
      )}
      <div
        className={`opacity-0 -mt-16 z-40 flex flex-col items-center justify-center rounded bg-purple-darker bg-center bg-no-repeat ${bgSize} transition pointer-events-none ${
          isPreSignupFlow
            ? 'fixed w-[93%] max-w-[620px] rounded h-3/4 max-h-[600px] top-13/20 left-1/2 -translate-x-1/2 -translate-y-1/2'
            : 'absolute inset-0'
        }`}
        onClick={handleClick}
        ref={ref}
        style={{
          backgroundImage: `url(${appConfig.assetsCdnUrl}/bootcamp/reward-chest-background.svg)`,
        }}
      >
        <div className="relative -mt-24">
          {showChest && (
            <RewardsChest
              hasBackgroundBubble={false}
              isLargeReward
              isRewardDelay
              onAnimationComplete={handleAnimationComplete}
              size={!isMobile ? 'large' : 'medium'}
              startAnimation
            />
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 mx-auto z-10">
          <RewardSection rewards={rewards} shouldHideRewardBackground={isPreSignupFlow} text={t('congratulations')} />
        </div>
      </div>
    </>
  )
}
